// src/api/AxiosApiCall.js
import axios from "axios";
import CryptoJS from "crypto-js";

// Create Axios instance
const axiosInstance = axios.create();

const AxiosApiCall = () => {
    // AES Encryption
    const encryptData = (data) => {
        // console.log(data)
        const secretKey = process.env.REACT_APP_ENCRYPTION_KEY;
        const iv = CryptoJS.enc.Hex.parse(process.env.REACT_APP_IV_KEY);

        const encrypted = CryptoJS.AES.encrypt(
            JSON.stringify(data),
            CryptoJS.enc.Utf8.parse(secretKey),
            {
                iv: iv,
                padding: CryptoJS.pad.Pkcs7,
                mode: CryptoJS.mode.CBC,
            }
        );

        return encrypted.toString();
    };

    // Add request interceptor
    axiosInstance.interceptors.request.use(
        (config) => {
            // Add authorization header
            const authData = JSON.parse(localStorage.getItem("auth") || "{}");
            if (authData?.token) {
                config.headers.Authorization = `Bearer ${authData.token}`;
            }

            const isEncryptionEnabled =
                // process.env.REACT_APP_ENCRYPTION_ENABLED;
                process.env.REACT_APP_ENCRYPTION_ENABLED === "true";

            const isModifyingMethod = ["post", "put", "delete", "patch"].includes(
                config.method?.toLowerCase()
            );

            // Handle encrypted request
            if (
                isEncryptionEnabled &&
                isModifyingMethod &&
                config.data &&
                !config.data._isEncrypted
            ) {
                if (config.data instanceof FormData) {
                    // Not encrypting, but still sending FormData
                    config.headers["Content-Type"] = "multipart/form-data";
                } else {
                    // Encrypt plain object
                    const encryptedData = encryptData(config.data);
                    config.data = {
                        request_data: encryptedData,
                        _isEncrypted: true,
                    };
                    config.headers["Content-Type"] = "application/json";
                }
            } else if (config.data instanceof FormData) {
                // Not encrypting, but still sending FormData
                config.headers["Content-Type"] = "multipart/form-data";
            } else {
                config.headers["Content-Type"] = "application/json";
            }

            return config;
        },
        (error) => Promise.reject(error)
    );

    // Force logout: clear session and hard-redirect to the login page.
    const forceLogout = () => {
        localStorage.removeItem("auth");
        localStorage.removeItem("user_id");
        localStorage.removeItem("portal");
        localStorage.removeItem("permissions");
        // Hard navigation so React state/routers are fully reset. replace()
        // prevents the user from going "back" into the authenticated app.
        window.location.replace("/");
    };

    // Detect a 401 whether it comes back as an HTTP status or in the JSON body
    // (the API returns HTTP 200 with { status: 401, message: "Unauthenticated." }).
    const isUnauthorized = (responseOrError) => {
        const httpStatus = responseOrError?.response?.status ?? responseOrError?.status;
        const bodyStatus =
            responseOrError?.response?.data?.status ?? responseOrError?.data?.status;
        return Number(httpStatus) === 401 || Number(bodyStatus) === 401;
    };

    // Add response interceptor to handle error statuses
    axiosInstance.interceptors.response.use(
        (response) => {
            // Check if response body contains an error status
            if (response.data?.status && response.data.status >= 400) {
                if (isUnauthorized(response)) {
                    forceLogout();
                    return Promise.reject(new Error(response.data?.message || "Unauthenticated."));
                }
                const error = new Error(response.data?.message || "An error occurred");
                error.response = response;
                return Promise.reject(error);
            }
            return response;
        },
        (error) => {
            if (isUnauthorized(error)) {
                forceLogout();
            }
            return Promise.reject(error);
        }
    );

    return axiosInstance;
};

export default AxiosApiCall;
